/**
 * RecorderStop — the waits of a live recorder's Stop, shared by the screen
 * recorder (js/screen-recorder/recorder.js) and the webcam recorder
 * (js/webcam-recorder/recorder.js). The webcam engine began as a clone of the
 * screen engine, and fixes to one stop path kept missing the other: the
 * MediaRecorder hardening of 09.07.2026 and the bounded waits of 26.09.2026
 * both landed in the screen recorder only. One implementation now.
 *
 *   RecorderStop.bounded(promise, ms)
 *       → { ok:true, value } | { ok:false, error } | { ok:false, timedOut:true }
 *       Never rejects, never waits longer than `ms` of wall time.
 *
 *   RecorderStop.settleMediaRecorder(mr, { watchdogMs })
 *       → Promise<'stop' | 'error' | 'inactive' | 'stop-throw' | 'watchdog'>
 *       Stops `mr` and settles on the first sign it is done.
 *
 * BOUNDED WAITS. A flush waits for every frame the encoder still holds.
 * Reproduced 26.09.2026 on the reporter's own build (Yandex 26.8) and on
 * Chrome 154: a tab take whose encoder had fallen behind held hundreds of
 * frames it processed at ~0 fps, and Stop sat in «Saving…» for 90 s and more
 * with no file and no error; with the GPU process lost mid-take the encoder
 * never answers at all and the wait is forever. Prod, 30 days: 53 screen takes
 * never reached record_finalize_ok after Stop. The clock is a worker's: a
 * hidden tab — which a recorder usually is during a take — stretches its own
 * timers to a second and, five minutes in, to a minute; a worker's interval
 * keeps wall time.
 *
 * MEDIARECORDER STOP. Waiting for the `stop` event alone hung forever on real
 * Firefox (feedback 09.07.2026, sid 20184c61 «не загружается видео после того
 * как записалось»): when the source tracks end before or around mr.stop() —
 * the browser's own «Stop sharing», a camera that went away — Firefox fires
 * `error` (or nothing) instead of `stop`. Four settle paths plus a watchdog:
 * the `stop` event; `error` (chunks up to the last timeslice are valid —
 * salvage them); already inactive (auto-stop raced us, its event may have
 * fired before the handler existed); stop() throwing; the watchdog.
 */
(function (w) {
    'use strict';
    if (w.RecorderStop) return;

    var waits = [], ticker = null;
    function idle() {
        if (!waits.length && ticker) { try { ticker.stop(); } catch (_) {} ticker = null; }
    }
    function tick() {
        var now = performance.now();
        for (var i = waits.length - 1; i >= 0; i--) {
            if (waits[i].at <= now) { var x = waits.splice(i, 1)[0]; try { x.done(); } catch (_) {} }
        }
        idle();
    }
    // One script URL for every ticker, never revoked: most bounded waits settle
    // in milliseconds, and revoking the URL of a worker that has not fetched its
    // script yet logs «An unknown error occurred when fetching the script»
    // (seen ×10 per take).
    var tickerUrl = null;
    function startTicker() {
        if (ticker) return;
        try {
            if (!tickerUrl) tickerUrl = URL.createObjectURL(new Blob(['setInterval(function(){postMessage(1)},100)'], { type: 'text/javascript' }));
            var wk = new Worker(tickerUrl);
            wk.onmessage = tick;
            ticker = { stop: function () { try { wk.terminate(); } catch (_) {} } };
        } catch (_) {
            var id = setInterval(tick, 100);
            ticker = { stop: function () { clearInterval(id); } };
        }
    }
    function bounded(p, ms) {
        var entry = null;
        var timer = new Promise(function (resolve) {
            entry = { at: performance.now() + ms, done: resolve };
            waits.push(entry);
            startTicker();
        });
        function settle() { var i = waits.indexOf(entry); if (i >= 0) waits.splice(i, 1); idle(); }
        return Promise.race([
            Promise.resolve(p).then(function (v) { settle(); return { ok: true, value: v }; },
                                    function (e) { settle(); return { ok: false, error: e }; }),
            timer.then(function () { return { ok: false, timedOut: true }; })
        ]);
    }

    function settleMediaRecorder(mr, o) {
        o = o || {};
        var ms = o.watchdogMs || 8000;
        return new Promise(function (resolve) {
            var done = false;
            var gate = { resolve: null };
            function fin(via) {
                if (done) return;
                done = true;
                if (gate.resolve) gate.resolve();
                resolve(via);
            }
            mr.onstop = function () { fin('stop'); };
            mr.onerror = function () { fin('error'); };
            // the watchdog is a bounded wait on a promise only fin() settles, so
            // it ends with the stop instead of ticking on for its full length
            bounded(new Promise(function (r) { gate.resolve = r; }), ms).then(function (res) { if (res.timedOut) fin('watchdog'); });
            if (mr.state === 'inactive') {
                // already stopped (source tracks ended): give a late dataavailable one beat
                setTimeout(function () { fin('inactive'); }, 50);
                return;
            }
            try { mr.stop(); } catch (_) { fin('stop-throw'); }
        });
    }

    w.RecorderStop = { bounded: bounded, settleMediaRecorder: settleMediaRecorder };
})(window);
